import { Divider, Text, vars } from "@vericus/cadmus-ui";

import { clsx } from "clsx";

import lockIllustration from "@/assets/illustrations/student-with-locked-browser.png";
import feedbackImg from "@/assets/illustrations/students-discussing.png";
import { __GLOBAL_STUDENT_ID } from "@/client/globals";
import { LoadingPage } from "@/components/loading-page";
import { useFeedbackSummaryQuery } from "@/generated/graphql";
import { useAssessmentParams, useRootNavigate } from "@/routes";

import { FeedbackItem } from "../components/feedback-item";
import { FeedbackResults } from "../components/feedback-results";
import { FeedbackItemContent } from "../utils/feedback-visibility";
import * as styles from "./home-page.css";

/**
 * Page on the aphrodite home route that displays the feedback summary before
 * the student can view the full feedback.
 */
export function FeedbackSummaryPage() {
  const rootNavigate = useRootNavigate();
  const { assessmentId, tenant } = useAssessmentParams();

  const { data, loading } = useFeedbackSummaryQuery({
    variables: {
      assessmentId,
    },
  });

  const assessment = data?.assessment;
  const settings = assessment?.settings;
  const groupWorkOutcome = data?.studentOutcome.groupWorkOutcome;
  const groupRelease = groupWorkOutcome?.workOutcomeRelease;
  const individualWorkOutcome = data?.studentOutcome.individualWorkOutcome;
  const individualRelease = individualWorkOutcome?.workOutcomeRelease;

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingPage />
      </div>
    );
  }

  if (!settings || !data?.studentOutcome) return null;

  const groupFeedbackReleased = !!groupRelease;
  const individualFeedbackReleased = !!individualRelease;
  const feedbackReleased = !!groupRelease || !!individualRelease;

  if (settings.feedbackDateDisabled) {
    return (
      <div className={styles.feedbackOverview}>
        <img src={feedbackImg} height={180} width={180} alt="" />
        <div>
          <Text
            kind="displayThree"
            textAlign="center"
            style={{ fontSize: "2rem" }}
          >
            <span style={{ color: vars.colors.lilac500 }}>Feedback</span> has
            been released via your LMS
          </Text>
          <Text kind="bodyMd" textAlign="center" style={{ maxWidth: 700 }}>
            Your grade has been released through the learning management system.
            Please refer to the LMS for your results. Written feedback was not
            provided for this assessment.
          </Text>
        </div>
      </div>
    );
  }

  if (!feedbackReleased) {
    return (
      <div className={styles.feedbackOverview}>
        <img src={lockIllustration} height={180} width={180} alt="" />
        <div>
          <Text
            kind="displayThree"
            textAlign="center"
            style={{ fontSize: "2rem" }}
          >
            <span style={{ color: vars.colors.lilac500 }}>Feedback</span> has
            not been released yet
          </Text>
          <Text kind="bodyMd" textAlign="center" style={{ maxWidth: 700 }}>
            Your grade will be released through Cadmus. <br />
            Written feedback will be provided for this assessment.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.feedbackMain}>
      <FeedbackResults
        feedbackViewLevel={data.finalFeedbackVisibility.feedbackViewLevel}
        finalGrade={data.studentOutcome.final.total?.weightedScore}
        maxGrade={settings.maxGrade}
      />
      <div className={clsx(styles.feedbackOverview, styles.feedbackRight)}>
        {settings.groupAssessmentEnabled && groupWorkOutcome && (
          <>
            <FeedbackItem
              id={FeedbackItemContent.Group}
              title="Group Work"
              feedbackVisibility={groupRelease?.feedbackVisibility ?? null}
              weight={data.studentOutcome.final.group?.weight ?? null}
              maxGrade={data.studentOutcome.final.group?.maxScore ?? null}
              finalScore={groupWorkOutcome.workOutcomeRelease?.finalScore}
              feedbackReleased={groupFeedbackReleased}
              onNavigateToFeedback={() => rootNavigate(`/feedback?tab=group`)}
              workId={groupWorkOutcome.workId}
              tenant={tenant}
            />
          </>
        )}
        {settings.individualReportEnabled && individualWorkOutcome && (
          <>
            <Divider />
            <FeedbackItem
              id={FeedbackItemContent.Individual}
              title="Individual Work"
              feedbackVisibility={individualRelease?.feedbackVisibility ?? null}
              weight={data.studentOutcome.final.individual?.weight ?? null}
              maxGrade={data.studentOutcome.final.individual?.maxScore ?? null}
              finalScore={individualWorkOutcome.workOutcomeRelease?.finalScore}
              feedbackReleased={individualFeedbackReleased}
              onNavigateToFeedback={() =>
                rootNavigate(`/feedback?tab=individual`)
              }
              workId={individualWorkOutcome.workId}
              tenant={tenant}
            />
          </>
        )}
        {settings.peerEvaluationEnabled && (
          <>
            <Divider />
            <FeedbackItem
              id={FeedbackItemContent.Peer}
              title="Peer Evaluation"
              feedbackVisibility={groupRelease?.feedbackVisibility ?? null}
              weight={data.studentOutcome.final.peer?.weight ?? null}
              maxGrade={data.studentOutcome.final.peer?.maxScore ?? null}
              finalScore={data.studentOutcome.final.peer?.score}
              feedbackReleased={groupFeedbackReleased}
              onNavigateToFeedback={() => rootNavigate(`/feedback?tab=peer`)}
              tenant={tenant}
            />
          </>
        )}
        {settings.selfEvaluationEnabled && (
          <>
            <Divider />
            <FeedbackItem
              id={FeedbackItemContent.Self}
              title="Self Evaluation"
              feedbackVisibility={groupRelease?.feedbackVisibility ?? null}
              weight={data.studentOutcome.final.self?.weight ?? null}
              maxGrade={data.studentOutcome.final.self?.maxScore ?? null}
              finalScore={data.studentOutcome.final.self?.score}
              feedbackReleased={groupFeedbackReleased}
              onNavigateToFeedback={() => rootNavigate(`/feedback?tab=peer`)}
              tenant={tenant}
            />
          </>
        )}
      </div>
    </div>
  );
}
